import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineChatAlt2, HiX } from 'react-icons/hi';
import notificationService from '../../services/notificationService';

const NotificationPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = async (skipCache = false) => {
    try {
      const result = await notificationService.getNotifications({ limit: 100, skipCache });
      if (result.success) {
        setNotifications(result.notifications || []);
        setUnreadCount(result.pagination?.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Setup polling on mount
  useEffect(() => {
    // Initial fetch
    fetchNotifications();

    // Poll every 60 seconds (skip cache to get fresh data)
    pollingIntervalRef.current = setInterval(() => {
      fetchNotifications(true);
    }, 60000);

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Toggle dropdown
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  // Mark notification as read
  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      const result = await notificationService.markAsRead(notification.id);
      if (result.success) {
        // Update local state
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, read: true, read_at: new Date().toISOString() } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    }
  };

  // Dismiss single notification
  const handleDismiss = async (e, notificationId) => {
    e.stopPropagation();
    setLoading(true);
    const result = await notificationService.dismissNotification(notificationId);
    if (result.success) {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      // Update unread count if it was unread
      const notification = notifications.find((n) => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    }
    setLoading(false);
  };

  // Clear all notifications
  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all notifications?')) {
      return;
    }
    setLoading(true);
    const result = await notificationService.dismissAll();
    if (result.success) {
      setNotifications([]);
      setUnreadCount(0);
    }
    setLoading(false);
  };

  // Format timestamp
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return `${Math.floor(diffDays / 7)}w ago`;
  };

  // Parse message for links and make them open in new window
  const renderMessage = (message) => {
    if (!message) return null;

    // Simple regex to detect URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = message.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Get notification type color
  const getTypeColor = (type) => {
    const colors = {
      billing: 'text-green-600 dark:text-green-400',
      subscription: 'text-purple-600 dark:text-purple-400',
      esim: 'text-blue-600 dark:text-blue-400',
      usage: 'text-orange-600 dark:text-orange-400',
      account: 'text-gray-600 dark:text-gray-400',
      general: 'text-gray-600 dark:text-gray-400',
    };
    return colors[type] || colors.general;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Messages Icon Button */}
      <button
        onClick={togglePanel}
        className="relative p-2 rounded-lg text-secondary hover:bg-accent transition-colors duration-200"
        aria-label="Notifications"
      >
        <HiOutlineChatAlt2 className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="fixed sm:absolute right-4 sm:right-0 left-4 sm:left-auto mt-2 sm:w-96 bg-primary border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 transition-colors duration-200">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-primary transition-colors duration-200">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-sm font-normal text-secondary transition-colors duration-200">
                  ({unreadCount} unread)
                </span>
              )}
            </h3>
            {notifications.length > 0 && (
              <button
                onClick={handleClearAll}
                disabled={loading}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-secondary transition-colors duration-200">
                <HiOutlineChatAlt2 className="h-12 w-12 mx-auto mb-2 text-gray-400 dark:text-gray-600" />
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 py-3 border-b border-gray-200 dark:border-gray-700 hover:bg-accent transition-colors duration-200 cursor-pointer ${
                    !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Title */}
                      <div className="flex items-center gap-2 mb-1">
                        <h4
                          className={`text-sm font-semibold ${
                            notification.read
                              ? 'text-secondary transition-colors duration-200'
                              : 'text-primary transition-colors duration-200'
                          }`}
                        >
                          {notification.title}
                        </h4>
                        {!notification.read && (
                          <span className="inline-block w-2 h-2 bg-blue-600 rounded-full"></span>
                        )}
                      </div>

                      {/* Message */}
                      <p className="text-sm text-secondary transition-colors duration-200 mb-2">
                        {renderMessage(notification.message)}
                      </p>

                      {/* Timestamp */}
                      <div className="flex items-center gap-2 text-xs text-secondary transition-colors duration-200">
                        <span>{formatDate(notification.created_at)}</span>
                        <span>•</span>
                        <span>{formatTimeAgo(notification.created_at)}</span>
                        {notification.type && (
                          <>
                            <span>•</span>
                            <span className={getTypeColor(notification.type)}>
                              {notification.type}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Dismiss Button */}
                    <button
                      onClick={(e) => handleDismiss(e, notification.id)}
                      disabled={loading}
                      className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                      aria-label="Dismiss"
                    >
                      <HiX className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
